<template>
    <kt-modal 
        :visible="recordVisible" 
        :title="recordTitle"
        :size="'modal-xl'" 
        @dismiss="onDismiss()"
    >
        <template #body>
            <estimate-form v-model="record"></estimate-form>
        </template>

        <template #footer>
            <div class="kt-modal-actions d-flex align-items-center justify-content-between w-100">
                <div class="form-check form-switch">
                    <input 
                        class="form-check-input" 
                        type="checkbox" 
                        id="sendNotificationToggleEst"
                        v-model="sendNotification"
                    >
                    <label class="form-check-label" for="sendNotificationToggleEst">
                        Send Update Notification
                    </label>
                </div>
                <div>
                    <button @click="onDismiss()" class="btn btn-outline-secondary">
                        <X :size="16" class="me-1" />Close
                    </button>
                    <button v-if="record.Id" @click="deleteRecord()" class="btn btn-outline-danger ms-2">
                        <Trash2 :size="16" class="me-1" />Delete
                    </button>
                    <button @click="saveRecord()" class="btn btn-primary ms-2">
                        <Save :size="16" class="me-1" />Save
                    </button>
                </div>
            </div>
        </template>
    </kt-modal>
</template>

<script lang="ts" setup>
import { ref, watch, onUnmounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import Api from "@/services/ApiService";
import Alert from "@/services/Alert";
import Toast from "@/services/Toast";
import Emitter from "@/services/Emitter";
import { WorkOrderModel } from "@/models/WorkOrderModel";
import KtModal from "@/components/UI/KtModal.vue";
import EstimateForm from "@/components/Estimates/EstimateForm.vue";
import { X, Trash2, Save } from 'lucide-vue-next';

const route = useRoute();
const router = useRouter();
const recordTitle = ref("Estimate");
const recordVisible = ref(true);
const unwatch: Array<Function> = [];

const emit = defineEmits(['close']);

const newRecord = () => {
    const rec = new WorkOrderModel();
    rec.Title = 'Estimate';  // Set Title to Estimate for new records
    return rec;
};

const record = ref<WorkOrderModel>(newRecord());
const sendNotification = ref(false);

const onDismiss = () => {
    // Check if we're in a route-based context or global modal
    if (route.params.id && route.name) {
        recordVisible.value = false;
        setTimeout(() => {
            router.push({ path: "/estimates" });
        }, 100);
    } else {
        // Global modal - emit close event
        emit('close');
    }
};

const saveRecord = async () => {
    try {
        // Validate
        if (!record.value.ContactId) {
            Alert.notify({ text: "Please select a client", type: "error" });
            return;
        }

        // Ensure Title is set to Estimate
        record.value.Title = 'Estimate';

        // Auto-generate estimate number if not provided
        if (!record.value.InvoiceNumber && record.value.Id) {
            const clientCode = record.value.Contact?.Code || '';
            record.value.InvoiceNumber = `EST-${clientCode}${record.value.Id}`;
        }

        // Save
        const saved = await Api.saveWorkOrder(record.value);
        
        // Auto-generate estimate number for new records (after first save)
        if (!saved.InvoiceNumber && saved.Id) {
            const clientCode = saved.Contact?.Code || '';
            saved.InvoiceNumber = `EST-${clientCode}${saved.Id}`;
            // Save again with the generated estimate number
            await Api.saveWorkOrder(saved);
        }
        
        // Reload the full estimate with all relationships (Items, Contact, etc.)
        if (saved.Id) {
            const fullRecord = await Api.loadWorkOrder(saved.Id);
            record.value = fullRecord;
        } else {
            record.value = saved;
        }
        
        Toast.success("Estimate saved successfully");
        
        // Refresh datatable
        Emitter.emit('datatable-refresh', 'estimate-list');
        Emitter.emit('datatable-refresh', 'estimate-embedded');
        
        // Update route if it was a new record
        if (route.params.id === 'new') {
            router.replace({ path: "/estimate/edit/" + saved.Id });
        }
    } catch (error) {
        console.error("Error saving estimate:", error);
        Alert.notify({ text: "Failed to save estimate", type: "error" });
    }
};

const deleteRecord = () => {
    Alert.confirm(
        'Are you sure you want to delete this estimate?',
        async () => {
            try {
                await Api.deleteWorkOrder(record.value.Id!);
                Toast.success("Estimate deleted");
                
                // Refresh datatables
                Emitter.emit('datatable-refresh', 'estimate-list');
                Emitter.emit('datatable-refresh', 'estimate-embedded');
                
                onDismiss();
            } catch (error) {
                console.error("Error deleting estimate:", error);
                Alert.notify({ text: "Failed to delete estimate", type: "error" });
            }
        },
        () => {} // No action on cancel
    );
};

// Watch for route param changes
unwatch.push(
    watch(
        () => route.params.id,
        async (newId) => {
            if (newId === 'new') {
                recordTitle.value = "New Estimate";
                record.value = newRecord();
            } else if (newId) {
                try {
                    const data = await Api.loadWorkOrder(Number(newId));
                    record.value = data;
                    recordTitle.value = `Estimate #${data.InvoiceNumber || data.Id}`;
                } catch (error) {
                    console.error("Error loading estimate:", error);
                    Alert.notify({ text: "Failed to load estimate", type: "error" });
                }
            }
        },
        { immediate: true }
    )
);

onUnmounted(() => {
    unwatch.forEach(fn => fn());
});
</script>
